import React from 'react';
import yaml from 'js-yaml';
import { Field, FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import {
  Alert,
  Flex,
  TabList,
  TabPanel,
  TabPanels,
  Box,
  Text,
  Img,
  Button,
  Collapse,
  Divider,
  Spinner,
  Icon,
  Heading,
  Tabs,
  Tab,
} from 'pounceV2';
import { MutationResult } from '@apollo/client';
import FormikEditorV3 from 'Components/fields/Editor/EditorV3';
import RenamableTabV3 from 'Components/RenamableTab/RenamableTabV3';
import DeleteTestModalV3 from 'Components/modals/DeleteTestModal/DeleteTestModalV3';
import {
  DetectionTestDefinitionInput,
  PutCorrelationRuleInput,
  TestCorrelationRuleYAMLTestResult,
} from 'Generated/schema';
import { logError } from 'Helpers/errors';
import { generateRandomColor, toPlural } from 'Helpers/utils';
import useModalV3 from 'Hooks/useModalV3';
import UndrawSVG from 'Assets/undraw-code-inspection-bdl7.svg';
import { useTheme } from '@mui/material';
import { TestCorrelationRule, useTestCorrelationRule } from './graphql/correlationRule.generated';
import { CorrelationRuleData } from './utils';

export const getStructuredTestDefinition = (loaded: any): DetectionTestDefinitionInput => {
  const ruleOutputs = (loaded.RuleOutputs || []).map(output => {
    // for sending a TestCorrelationRule call to graphql, these fields must be lowercase.
    return { id: output.Id, matches: output.Matches };
  });

  return {
    name: loaded.Name || '',
    expectedResult: loaded.ExpectedResult || false,
    resource: 'null',
    mocks: [],
    ruleOutputs,
  };
};

export const getUnstructuredTestDefinition = (
  tc: any,
  loaded: any,
): DetectionTestDefinitionInput => {
  return {
    name: loaded.Name || '',
    expectedResult: loaded.ExpectedResult || false,
    resource: JSON.stringify({ testCase: tc.resource }),
    mocks: [],
    ruleOutputs: [],
  };
};

/// if useStructuredFormat, returns a DetectionTestDefinitionInput whose test body is structured
/// in the ruleOutputs field. this is used for sending a request to test the test case.
/// otherwise, if unstructured, the entire test case body is put into the resource string.
/// this is used for storing the test in the database such that we preserve comments and formatting.
/// returns tuple of [the validated test case, and whether or not it passed validation]
export const getValidatedTestCase = (
  tc: any,
  useStructuredFormat?: boolean,
): [DetectionTestDefinitionInput, boolean] => {
  try {
    const loaded = (yaml.load(tc.resource || '') as any) || {};

    const newTest = useStructuredFormat
      ? getStructuredTestDefinition(loaded)
      : getUnstructuredTestDefinition(tc, loaded);

    if (!newTest.name) {
      return [newTest, false];
    }
    if (!loaded.RuleOutputs) {
      return [newTest, false];
    }
    return [newTest, true];
  } catch (e) {
    logError(e);
    return [null, false];
  }
};

export const getSerializedTestCase = (
  tc: DetectionTestDefinitionInput,
): DetectionTestDefinitionInput => {
  if (tc.resource.includes('testCase')) {
    let resource = '# failed to parse test case!';
    try {
      const extractedResource = JSON.parse(tc.resource);
      if (extractedResource.testCase) {
        resource = extractedResource.testCase;
      }
    } catch (e) {
      logError(e);
    }

    return {
      name: tc.name,
      expectedResult: tc.expectedResult,
      ruleOutputs: [],
      mocks: [],
      resource,
    };
  }

  // backwards compatibility: if PAT uploaded a unit test, it has ruleOutputs,
  // then we create a string from the structured output.
  const ruleOutputs = yaml.dump(tc.ruleOutputs || {}, {
    replacer: (key, value) => {
      // yaml serialization includes the typename as it comes from graphql.
      // we remove this as we dont want this part of the test case shown to the user.
      if (key === '__typename') {
        return undefined;
      }
      return value;
    },
  });
  const resource = `Name: ${tc.name}
ExpectedResult: ${tc.expectedResult}
RuleOutputs:\n${ruleOutputs}`;
  return {
    name: tc.name,
    expectedResult: tc.expectedResult,
    ruleOutputs: tc.ruleOutputs,
    mocks: tc.mocks,
    resource,
  };
};

export const getMappedSerializedTestCases = (
  tests?: DetectionTestDefinitionInput[],
): DetectionTestDefinitionInput[] => {
  if (!tests) {
    return [];
  }
  return tests.map(tc => getSerializedTestCase(tc));
};

export const getMappedTestCases = (
  tests?: any[],
  useStructuredFormat?: boolean,
): DetectionTestDefinitionInput[] => {
  if (!tests) {
    return [];
  }
  return tests?.map(tc => getValidatedTestCase(tc, useStructuredFormat)[0]);
};

export const generateRuleOutputTemplate = (id: string): string => {
  return `  - ID: ${id}
    Matches:
      # field_name_mached_on:
        # field_value_matched_on: ["2006-01-02T15:04:05Z", "2006-01-02T15:04:06Z"]
          # note: timestamps can be absolute or relative in minutes, e.g., [0, 1]`;
};

/// generates a yaml string of a default test case to show the user
export const generateTestCaseYaml = (
  tc: DetectionTestDefinitionInput,
  parsedYaml: CorrelationRuleData,
): string => {
  let nodes = [];
  if (parsedYaml?.Sequence) {
    nodes = parsedYaml?.Sequence;
  } else if (parsedYaml?.Group) {
    nodes = parsedYaml?.Group;
  }

  if (!nodes.length) {
    // the user's correlation rule yaml failed to parse or is otherwise somehow invalid.
    // generate a default template in this case.
    return `# fill in your test case below:
Name: ${tc.name}
ExpectedResult: ${tc.expectedResult}
RuleOutputs:
  - ID: MyFirstRule
    Matches:
    # field_name_mached_on:
        # field_value_matched_on: ["2006-01-02T15:04:05Z", "2006-01-02T15:04:06Z"]
          # note: timestamps can be absolute or relative in minutes, e.g., [0, 1]
`;
  }

  return `Name: ${tc.name}
ExpectedResult: ${tc.expectedResult}
RuleOutputs:
${nodes
  .map(node => {
    const id = node?.ID ? node?.ID : 'id.from.your.correlation.rule';
    return generateRuleOutputTemplate(id);
  })
  .join('\n')}
`;
};

export const addNewTestHelper = (
  arrayHelpers: FieldArrayRenderProps,
  parsedYaml: CorrelationRuleData,
) => {
  const newTest: DetectionTestDefinitionInput = {
    name: `Test-${generateRandomColor()}`,
    expectedResult: true,
    resource: '',
    mocks: [],
    ruleOutputs: [],
  };
  newTest.resource = generateTestCaseYaml(newTest, parsedYaml);
  // adds a test to the front
  arrayHelpers.unshift(newTest);
};

const ViewResultsHeading = ({ children }) => <Heading size="small">{children}</Heading>;

const getNumTestsSucceeded = (testResults: MutationResult<TestCorrelationRule>) => {
  const { data, error } = testResults;
  let succeeded = 0;
  if (error) {
    return succeeded;
  }
  succeeded = data?.testCorrelationRule?.results?.filter(x => x.passed).length;
  return succeeded;
};

const PassedOrFailedTest = ({
  name,
  passed,
  onClick,
}: {
  name: string;
  passed: boolean;
  onClick: () => void;
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  return (
    <Flex>
      {passed ? (
        <Icon
          data-testid="test-icon-pass"
          color={tokens.content.assets.semantic.success.base}
          type="check-outline"
        />
      ) : (
        <Icon
          data-testid="test-icon-fail"
          color={tokens.content.assets.semantic.error.base}
          type="close-outline"
        />
      )}
      <Button variant="link-primary" p={0} ml={2} onClick={onClick}>
        {name}
      </Button>
    </Flex>
  );
};

const NumTestsPassedOrFailed = ({
  numTestsPassed,
  numTestsRan,
  isHeading = false,
}: {
  numTestsPassed: number;
  numTestsRan: number;
  isHeading?: boolean;
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  const testsWord = toPlural('test', numTestsRan);
  const Component = isHeading ? ViewResultsHeading : Text;
  if (numTestsRan === 1) {
    return (
      <Flex gap={2} align="center">
        {!isHeading && (
          <Icon
            color={
              numTestsPassed
                ? tokens.content.assets.semantic.success.base
                : tokens.content.assets.semantic.error.base
            }
            type={numTestsPassed ? 'check-outline' : 'close-outline'}
          />
        )}
        <Component data-testid={`single-result-${numTestsPassed ? 'passed' : 'failed'}`}>
          {numTestsPassed ? 'Test Passed!' : 'Test Failed'}
        </Component>
      </Flex>
    );
  }
  if (numTestsPassed === numTestsRan) {
    return (
      <Flex gap={2} align="center">
        {!isHeading && (
          <Icon color={tokens.content.assets.semantic.success.base} type="check-outline" />
        )}
        <Component>All tests passed!</Component>
      </Flex>
    );
  }

  const numTestsFailed = numTestsRan - numTestsPassed;
  return (
    <Flex gap={2} align="center">
      {!isHeading && (
        <Icon color={tokens.content.assets.semantic.error.base} type="close-outline" />
      )}
      <Component>
        {numTestsFailed ? `${numTestsFailed} of ${numTestsRan} ${testsWord} failed` : null}
      </Component>
    </Flex>
  );
};

type NamedTestResult = {
  name: string;
  passed: boolean;
};

const ScrollShadowBox = ({ isTop, shouldShadow }: { isTop: boolean; shouldShadow: boolean }) => {
  const marginTop = isTop ? '16px' : '-16px';
  const backgroundDirection = isTop ? 'to top' : 'to bottom';
  return (
    <Box
      sx={{
        marginTop,
        pointerEvents: 'none',
        width: '100%',
        height: '16px',
        background: `linear-gradient(${backgroundDirection}, rgba(24, 31, 43, 0.00) 0%, #181f2b 100%)`,
        opacity: shouldShadow ? 1 : 0,
        zIndex: 1,
      }}
    />
  );
};

const ScrollableResultsSection = ({
  testResults,
  onTestSelect,
}: {
  testResults: MutationResult<TestCorrelationRule>;
  onTestSelect: (string) => void;
}) => {
  const {
    values: { tests },
  } = useFormikContext<PutCorrelationRuleInput>();
  const testResultsArray = React.useMemo(() => {
    const namedResults = tests.map(test => {
      const result = testResults.data?.testCorrelationRule?.results.find(r => r.name === test.name);
      return {
        name: test.name,
        passed: result ? result.passed : false,
      } as NamedTestResult;
    });
    const failed = namedResults.filter(r => !r.passed);
    const passed = namedResults.filter(r => r.passed);
    return failed.concat(passed);
  }, [tests, testResults]);

  const scrollCardThreshold = 7; // number of results to show before enabling scrolling
  const scrollOffset = 1; // pixel offset to determine if scrolled past the beginning or end
  const scrollableContainer = React.useRef(null);
  const [isNearBeginning, setIsNearBeginning] = React.useState(true);
  const [isNearEnd, setIsNearEnd] = React.useState(false);
  const isScrollable = React.useMemo(
    () => testResultsArray.length >= scrollCardThreshold,
    [testResultsArray],
  );
  React.useEffect(() => {
    const container = scrollableContainer.current;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setIsNearBeginning(scrollTop <= scrollOffset);
      setIsNearEnd(scrollHeight - scrollTop <= clientHeight + scrollOffset);
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <ScrollShadowBox shouldShadow={isScrollable && !isNearBeginning} isTop={true} />
      <Flex
        ref={scrollableContainer}
        marginTop="-16px"
        flexDirection="column"
        spacing={2}
        maxHeight="20vh"
        overflowY="scroll"
      >
        {testResultsArray.map(tr => (
          <PassedOrFailedTest
            key={tr.name}
            name={tr.name}
            passed={tr.passed}
            onClick={() => onTestSelect(tr.name)}
          />
        ))}
      </Flex>
      <ScrollShadowBox shouldShadow={isScrollable && !isNearEnd} isTop={false} />
    </>
  );
};

const RunTestsResultsCollapse = ({
  resultsPanelIsOpen,
  setResultsPanelIsOpen,
  testResults,
  testsBeingRan,
  onTestSelect,
}: {
  resultsPanelIsOpen: boolean;
  testResults: MutationResult<TestCorrelationRule>;
  testsBeingRan: number;
  setResultsPanelIsOpen: (boolean) => void;
  onTestSelect: (string) => void;
}) => {
  const numSucceeded = React.useMemo(() => getNumTestsSucceeded(testResults), [testResults]);
  return (
    <Collapse isOpen={resultsPanelIsOpen}>
      <Flex paddingLeft={16} paddingRight={16} paddingTop={16} flexDirection="column">
        <Flex justify="space-between">
          <NumTestsPassedOrFailed
            numTestsPassed={numSucceeded}
            numTestsRan={testsBeingRan}
            isHeading={true}
          />
          <Button variant="link-primary" p={0} onClick={() => setResultsPanelIsOpen(false)}>
            Hide results
          </Button>
        </Flex>
        {resultsPanelIsOpen && (
          <ScrollableResultsSection testResults={testResults} onTestSelect={onTestSelect} />
        )}
      </Flex>
    </Collapse>
  );
};

const EMPTY_TEST_NAME_IDENT = '\n\n\n';

const RunTestsResults = ({
  testResults,
  testsBeingRan,
  resultsPanelIsOpen,
  setResultsPanelIsOpen,
}: {
  testResults: MutationResult<TestCorrelationRule>;
  testsBeingRan: number;
  resultsPanelIsOpen: boolean;
  setResultsPanelIsOpen: (boolean) => void;
}) => {
  const testsWord = toPlural('test', testsBeingRan);
  const { loading, data, error } = testResults;
  const numSucceeded = React.useMemo(() => getNumTestsSucceeded(testResults), [testResults]);

  if (resultsPanelIsOpen) {
    return null;
  }

  return (
    <Flex justifyContent="center" align="center" justify="center">
      {loading && (
        <>
          <Spinner size="small" mr={2} />
          <Text>
            Running {testsBeingRan} {testsWord}...
          </Text>
        </>
      )}
      {!loading && (data || error) && (
        <Flex gap={2}>
          <NumTestsPassedOrFailed numTestsPassed={numSucceeded} numTestsRan={testsBeingRan} />
          {data?.testCorrelationRule?.results?.length > 1 ? (
            <Button variant="link-primary" p={0} onClick={() => setResultsPanelIsOpen(true)}>
              View results
            </Button>
          ) : null}
        </Flex>
      )}
    </Flex>
  );
};

const RunTestsPanel = ({
  activeTabIndex,
  onTestSelect,
  testCorrelationRule,
  testResults,
}: {
  activeTabIndex: number;
  onTestSelect: (string) => void;
  testCorrelationRule: (params) => void;
  testResults: MutationResult<TestCorrelationRule>;
}) => {
  const {
    values: { tests, simpleDetectionBody },
  } = useFormikContext<PutCorrelationRuleInput>();

  const [testsBeingRan, setTestsBeingRan] = React.useState(1);
  const [resultsPanelIsOpen, setResultsPanelIsOpen] = React.useState(false);
  const runTests = React.useCallback(
    (index: number) => {
      if (index >= 0) {
        setTestsBeingRan(1);
      } else {
        setTestsBeingRan(tests.length);
      }
      setResultsPanelIsOpen(false);
      const testsToRun = index >= 0 ? [tests[index]] : tests;
      testCorrelationRule({
        errorPolicy: 'all',
        onError: () => {
          return null;
        },
        variables: {
          input: {
            tests: getMappedTestCases(testsToRun, false),
            simpleDetectionBody,
          },
        },
      });
    },
    [testCorrelationRule, tests, simpleDetectionBody, setTestsBeingRan, setResultsPanelIsOpen],
  );

  // dont show "run test" buttons if no tests to run!
  if (!tests.length) {
    return null;
  }

  return (
    <Box>
      <Divider />
      <RunTestsResultsCollapse
        setResultsPanelIsOpen={setResultsPanelIsOpen}
        resultsPanelIsOpen={resultsPanelIsOpen}
        testResults={testResults}
        testsBeingRan={testsBeingRan}
        onTestSelect={onTestSelect}
      />
      <Flex gap={4}>
        <Button
          onClick={() => {
            runTests(activeTabIndex);
          }}
          variant="secondary"
        >
          Run Test
        </Button>
        <Button
          onClick={() => {
            runTests(-1);
          }}
          variant="secondary"
        >
          Run All Tests
        </Button>
        {!testResults?.error ? (
          <RunTestsResults
            resultsPanelIsOpen={resultsPanelIsOpen}
            setResultsPanelIsOpen={setResultsPanelIsOpen}
            testResults={testResults}
            testsBeingRan={testsBeingRan}
          />
        ) : null}
      </Flex>
    </Box>
  );
};

const UnitTestEditorPanel = ({
  activeTabIndex,
  parsedYaml,
  setActiveTabIndex,
  testResults,
  readOnly = false,
}: {
  activeTabIndex: number;
  parsedYaml: CorrelationRuleData;
  setActiveTabIndex: (number) => void;
  testResults: MutationResult<TestCorrelationRule>;
  readOnly?: boolean;
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  const listOfTestResults: TestCorrelationRuleYAMLTestResult[] =
    testResults?.data?.testCorrelationRule?.results;
  const [selectedText, setSelectedText] = React.useState('');

  // highlight the name of the test in the editor when user clicks rename
  const getEditorCb = React.useCallback(
    editor => {
      if (!selectedText) {
        return;
      }
      const searchText = selectedText === EMPTY_TEST_NAME_IDENT ? 'Name:' : selectedText;
      const matches = editor
        .getModel()
        ?.findMatches(searchText, false, false, true, null, false, 1);
      if (!matches || !matches.length) {
        return;
      }
      const firstMatch = matches[0];
      if (selectedText === EMPTY_TEST_NAME_IDENT) {
        // if user cleared out test name, put cursor next to "Name:" as a fallback
        editor.setPosition(firstMatch.range.getEndPosition());
      } else {
        // otherwise, select the current test name
        editor.setSelection(firstMatch.range);
      }
      editor.focus();
      // reset this such that if user switches tabs and back, we dont try to select the test name again.
      setSelectedText('');
    },
    [selectedText, setSelectedText],
  );

  // Read the values from the "parent" form. We expect a formik to be declared in the upper scope
  // since this is a "partial" form. If no Formik context is found this will error out intentionally
  const {
    values: { tests },
    validateForm,
    setFieldValue,
  } = useFormikContext<PutCorrelationRuleInput>();
  const { showModal } = useModalV3();
  const testsCount = tests?.length || 0;
  const testsExist = testsCount > 0;

  const unitTestEditorProps = React.useMemo(() => {
    return {
      as: FormikEditorV3,
      wordBasedSuggestions: false,
      language: 'yaml',
      required: true,
      hideScrollbar: true,
      readOnly,
    };
  }, [readOnly]);

  // test name source of truth comes from the yaml editor
  React.useEffect(() => {
    if (!tests || !tests.length) {
      return;
    }
    const test = tests[activeTabIndex];
    if (!test?.resource) {
      return;
    }

    try {
      const loadedObj = yaml.load(test.resource) as any;
      const testName = loadedObj?.Name ?? '';
      setFieldValue(`tests[${activeTabIndex}].name`, testName);
    } catch (e) {
      logError(e);
    }
  }, [tests, activeTabIndex, setFieldValue]);

  return (
    <FieldArray
      name="tests"
      render={arrayHelpers => {
        const handleTestRemoval = (e: React.MouseEvent, index: number) => {
          // the button is part of the "Tab" so we don't want to "navigate" to this tab
          // but only close it. Thus, we can't let the click event propagate.
          e.stopPropagation();
          showModal(
            <DeleteTestModalV3
              test={tests[index]}
              onConfirm={() => {
                // set index to the one to the left
                setActiveTabIndex(index === 0 ? 0 : index - 1);
                // removes the test
                arrayHelpers.remove(index);
                // There is currently a bug with Formik v2 and removing an item causes a wrong
                // `errors` state to be present. We manually kick in validation to fix that.
                // https://github.com/jaredpalmer/formik/issues/1616
                setTimeout(() => {
                  validateForm(['tests']);
                }, 200);
              }}
            />,
            { title: 'Delete Test' },
          );
        };

        if (!testsExist) {
          return (
            <Flex direction="column" align="center" width="100%" height="100%">
              <Img
                src={UndrawSVG}
                alt="undraw-code-inspection"
                nativeHeight={200}
                nativeWidth={200}
                marginY={10}
              />
              {!readOnly ? (
                <Flex display="inline" align="center">
                  <Button
                    variant="link-primary"
                    size="medium"
                    p={0}
                    onClick={() => addNewTestHelper(arrayHelpers, parsedYaml)}
                  >
                    Add a new unit test&nbsp;
                  </Button>
                  <Text as="span" color={tokens.content.text.neutral[3]}>
                    to start testing your correlation rule
                  </Text>
                </Flex>
              ) : (
                <Text as="span" color={tokens.content.text.neutral[3]} mb={8}>
                  There are no Unit Tests
                </Text>
              )}
            </Flex>
          );
        }

        return (
          <Box maxWidth="50vw" height="100%" overflowY="hidden">
            <Tabs
              as={Flex}
              direction="column"
              overflowY="hidden"
              height="100%"
              index={activeTabIndex}
              onChange={index => setActiveTabIndex(index)}
              variant="underline"
            >
              <TabList variant="scrollable" scrollButtons="auto" px={2} mt={2}>
                {tests.map((test, index) => (
                  <RenamableTabV3
                    useIcons={true}
                    key={`detection-test-tab-${index}`}
                    name={`tests[${index}].name`}
                    validateLocalOnly={true}
                    onEditOverride={() => {
                      if (!test.name || test.name.trim().length === 0) {
                        setSelectedText(EMPTY_TEST_NAME_IDENT);
                      } else {
                        setSelectedText(test.name);
                      }
                    }}
                    onDelete={e => {
                      handleTestRemoval(e, index);
                    }}
                    component={Tab}
                    uniqueCheck={tests.map(t => t.name)}
                    errorMessage="No duplicate test names"
                    canEdit={!readOnly}
                  />
                ))}
              </TabList>
              <Divider mt={0} mb={4} />
              <TabPanels style={{ flexGrow: '1', overflowY: 'scroll' }}>
                {tests.map((test, index) => {
                  const testResult = listOfTestResults?.find(result => result.name === test.name);
                  return (
                    <TabPanel key={`detection-test-body-${index}`}>
                      {testResult ? (
                        <Box m={4}>
                          <Alert
                            variant={testResult?.passed ? 'success' : 'error'}
                            title={testResult?.passed ? 'Test Passed' : 'Test Failed'}
                            description={testResult?.error}
                          />
                        </Box>
                      ) : null}
                      <Field
                        {...unitTestEditorProps}
                        name={`tests[${index}].resource`}
                        getEditorCb={index === activeTabIndex ? getEditorCb : undefined}
                      />
                    </TabPanel>
                  );
                })}
              </TabPanels>
            </Tabs>
          </Box>
        );
      }}
    />
  );
};

const UnitTestPanelV3 = ({
  parsedYaml,
  readOnly,
}: {
  parsedYaml: CorrelationRuleData;
  readOnly?: boolean;
}) => {
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const [testCorrelationRule, testResults] = useTestCorrelationRule({
    context: { failSilently: true },
  });
  const {
    values: { tests },
  } = useFormikContext<PutCorrelationRuleInput>();

  return (
    <Flex
      justifyContent="space-between"
      direction="column"
      height="100%"
      overflowY="hidden"
      pt={0}
      p={2}
    >
      {testResults?.error ? (
        <Box mx={2}>
          <Alert
            variant="error"
            title="Invalid test"
            description={testResults?.error?.message}
            lineLimit={3}
          />
        </Box>
      ) : null}
      <UnitTestEditorPanel
        parsedYaml={parsedYaml}
        activeTabIndex={activeTabIndex}
        setActiveTabIndex={setActiveTabIndex}
        testResults={testResults}
        readOnly={readOnly}
      />
      <RunTestsPanel
        activeTabIndex={activeTabIndex}
        onTestSelect={testName =>
          setActiveTabIndex(tests.map(result => result.name).indexOf(testName))
        }
        testCorrelationRule={testCorrelationRule}
        testResults={testResults}
      />
    </Flex>
  );
};

export default UnitTestPanelV3;
